import { useState, useEffect, useCallback } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import axios from 'axios'

const API = import.meta.env.VITE_API_GATEWAY_URL

async function authHeaders() {
    const session = await fetchAuthSession()
    return { Authorization: `Bearer ${session.tokens.idToken.toString()}` }
}

function ModulosManager() {
    const [empresas, setEmpresas] = useState([])
    const [catalogo, setCatalogo] = useState([])
    const [selectedEmpresa, setSelectedEmpresa] = useState(null)
    const [empresaModulos, setEmpresaModulos] = useState([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [seeding, setSeeding] = useState(false)
    const [msg, setMsg] = useState(null)

    const handleSeed = async () => {
        setSeeding(true); setMsg(null)
        try {
            const headers = await authHeaders()
            await axios.post(`${API}/admin/seed`, {}, { headers })
            setMsg({ type: 'success', text: '✅ Catálogo reiniciado. Nuevos módulos registrados.' })
            await loadBase()
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.error || err.message })
        } finally { setSeeding(false) }
    }

    const loadBase = useCallback(async () => {
        setLoading(true)
        try {
            const headers = await authHeaders()
            const [empRes, catRes] = await Promise.all([
                axios.get(`${API}/admin/empresas`, { headers }),
                axios.get(`${API}/admin/modulos`, { headers }),
            ])
            setEmpresas(empRes.data.empresas || [])
            setCatalogo(catRes.data.modulos || [])
        } catch (err) {
            setMsg({ type: 'error', text: 'Error cargando datos: ' + err.message })
        } finally {
            setLoading(false)
        }
    }, [])

    const loadEmpresaModulos = useCallback(async (empresaId) => {
        try {
            const headers = await authHeaders()
            const res = await axios.get(`${API}/admin/empresas/${empresaId}/modulos`, { headers })
            setEmpresaModulos(res.data.modulos || [])
        } catch (err) {
            setMsg({ type: 'error', text: 'Error cargando módulos de la empresa' })
        }
    }, [])

    useEffect(() => { loadBase() }, [loadBase])

    const handleSelectEmpresa = async (empresa) => {
        setSelectedEmpresa(empresa)
        await loadEmpresaModulos(empresa.id)
    }

    const isModuloActivo = (moduloId) => {
        const m = empresaModulos.find(em => em.id === moduloId)
        return m ? m.activo : false
    }

    const handleToggle = (moduloId) => {
        setEmpresaModulos(prev => {
            const existing = prev.find(m => m.id === moduloId)
            if (existing) {
                return prev.map(m => m.id === moduloId ? { ...m, activo: !m.activo } : m)
            }
            return [...prev, { id: moduloId, activo: true }]
        })
    }

    const handleSave = async () => {
        if (!selectedEmpresa) return
        setSaving(true)
        setMsg(null)
        try {
            const headers = await authHeaders()
            // Asegurarse de que todos los módulos del catálogo estén en la lista
            const modulosCompletos = catalogo.map(cat => ({
                id: cat.id,
                activo: isModuloActivo(cat.id)
            }))
            await axios.post(
                `${API}/admin/empresas/${selectedEmpresa.id}/modulos`,
                { modulos: modulosCompletos },
                { headers }
            )
            setMsg({ type: 'success', text: `Módulos de "${selectedEmpresa.nombre}" actualizados correctamente` })
            await loadEmpresaModulos(selectedEmpresa.id)
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.error || err.message })
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="admin-section">
            <div className="admin-section-header">
                <div>
                    <h2>🧩 Módulos</h2>
                    <p>Asigna módulos/herramientas a cada empresa</p>
                </div>
                <button
                    className="btn-secondary"
                    onClick={handleSeed}
                    disabled={seeding}
                    title="Registra o actualiza el catálogo de módulos disponibles en la base de datos"
                >
                    {seeding ? '⏳ Actualizando...' : '🔄 Reiniciar catálogo'}
                </button>
            </div>

            {msg && (
                <div className={`admin-alert admin-alert--${msg.type}`}>
                    {msg.text} <button onClick={() => setMsg(null)}>✕</button>
                </div>
            )}

            {loading ? (
                <div className="admin-loading-inline"><div className="admin-spinner-sm"></div> Cargando...</div>
            ) : (
                <div className="modulos-layout">
                    {/* Panel izquierdo: lista de empresas */}
                    <div className="empresa-list-panel">
                        <h3>Selecciona empresa</h3>
                        {empresas.length === 0 ? (
                            <p className="no-value">Crea empresas primero</p>
                        ) : (
                            empresas.map(emp => (
                                <button
                                    key={emp.id}
                                    className={`empresa-list-item ${selectedEmpresa?.id === emp.id ? 'selected' : ''} ${!emp.activa ? 'inactive' : ''}`}
                                    onClick={() => handleSelectEmpresa(emp)}
                                >
                                    <span className="empresa-list-nombre">{emp.nombre}</span>
                                    <span className="empresa-list-id">{emp.id}</span>
                                    {!emp.activa && <span className="empresa-list-badge">Inactiva</span>}
                                </button>
                            ))
                        )}
                    </div>

                    {/* Panel derecho: módulos de la empresa */}
                    <div className="modulos-panel">
                        {!selectedEmpresa ? (
                            <div className="admin-empty">
                                <span>🏢</span>
                                <p>Selecciona una empresa para gestionar sus módulos</p>
                            </div>
                        ) : (
                            <>
                                <div className="modulos-panel-header">
                                    <h3>Módulos para: <span className="highlight">{selectedEmpresa.nombre}</span></h3>
                                    <button className="btn-primary" onClick={handleSave} disabled={saving}>
                                        {saving ? 'Guardando...' : '💾 Guardar cambios'}
                                    </button>
                                </div>
                                <div className="modulos-grid">
                                    {catalogo.map(mod => {
                                        const activo = isModuloActivo(mod.id)
                                        return (
                                            <div
                                                key={mod.id}
                                                className={`modulo-card ${activo ? 'modulo-card--active' : ''}`}
                                                onClick={() => handleToggle(mod.id)}
                                            >
                                                <div className="modulo-card-top">
                                                    <span className="modulo-icon">{mod.icono || '🔧'}</span>
                                                    <div className={`modulo-toggle ${activo ? 'on' : 'off'}`}>
                                                        <div className="modulo-toggle-thumb"></div>
                                                    </div>
                                                </div>
                                                <div className="modulo-card-body">
                                                    <h4>{mod.nombre}</h4>
                                                    <p>{mod.descripcion}</p>
                                                    <span className="modulo-categoria">{mod.categoria}</span>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ModulosManager
